/**
 * src/pages/UserDetail.js
 * /users/:id — view and edit a single user
 *
 * Read mode: shows all user fields as static text
 * Edit mode: allows changing role, enabled status, and optionally password
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

// ---------------------------------------------------------------------------
// Cancel dialog
// ---------------------------------------------------------------------------

function CancelDialog({ onSave, onDiscard, onClose, saving }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ width: '420px', boxShadow: 'var(--shadow-lg)' }}>
        <div className="card-header">
          <span className="card-title">Unsaved Changes</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
            You have unsaved changes. What would you like to do?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}
              style={{ justifyContent: 'center' }}>
              Save Changes
            </button>
            <button className="btn btn-danger" onClick={onDiscard}
              style={{ justifyContent: 'center' }}>
              Discard Changes
            </button>
            <button className="btn btn-ghost" onClick={onClose}
              style={{ justifyContent: 'center' }}>
              Continue Editing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Edit form state
  const [formRole, setFormRole] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formPassword, setFormPassword] = useState('');
  const [formPasswordConfirm, setFormPasswordConfirm] = useState('');
  const [originalForm, setOriginalForm] = useState(null);

  async function fetchUser() {
    setError('');
    setLoading(true);
    try {
      const res = await api.get(`/portal/users/${id}`);
      setUser(res.data);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load user.');
      }
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUser(); }, [id]);

  function enterEditMode() {
    setFormRole(user.role);
    setFormEnabled(user.enabled);
    setFormPassword('');
    setFormPasswordConfirm('');
    const snapshot = JSON.stringify({ role: user.role, enabled: user.enabled, password: '' });
    setOriginalForm(snapshot);
    setIsEditing(true);
    setError('');
    setSuccess('');
  }

  const isDirty = useCallback(() => {
    if (!originalForm) return false;
    const current = JSON.stringify({ role: formRole, enabled: formEnabled, password: formPassword });
    return current !== originalForm;
  }, [originalForm, formRole, formEnabled, formPassword]);

  function handleCancelClick() {
    if (isDirty()) {
      setPendingNavigation(null);
      setShowCancelDialog(true);
    } else {
      exitEditMode(null);
    }
  }

  function exitEditMode(destination) {
    setIsEditing(false);
    setShowCancelDialog(false);
    setPendingNavigation(null);
    setError('');
    if (destination) {
      navigate(destination);
    }
  }

  async function handleSave() {
    setError('');

    if (formPassword && formPassword !== formPasswordConfirm) {
      setError('Passwords do not match');
      return;
    }
    if (formPassword) {
      if (formPassword.length < 12) { setError('Password must be at least 12 characters'); return; }
      if (!/[A-Z]/.test(formPassword)) { setError('Password must contain at least one uppercase letter'); return; }
      if (!/[a-z]/.test(formPassword)) { setError('Password must contain at least one lowercase letter'); return; }
      if (!/[0-9]/.test(formPassword)) { setError('Password must contain at least one digit'); return; }
    }

    setSaving(true);
    setShowCancelDialog(false);

    const payload = { role: formRole, enabled: formEnabled };
    if (formPassword) payload.password = formPassword;

    try {
      const res = await api.put(`/portal/users/${id}`, payload);
      setUser(prev => ({ ...prev, ...res.data }));
      setSuccess('User updated successfully.');
      setIsEditing(false);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to update user.');
      }
    } finally {
      setSaving(false);
    }
  }

  function handleBreadcrumbClick(e, destination) {
    e.preventDefault();
    if (isEditing && isDirty()) {
      setPendingNavigation(destination);
      setShowCancelDialog(true);
    } else if (isEditing) {
      setIsEditing(false);
      navigate(destination);
    } else {
      navigate(destination);
    }
  }

  const isSelf = user?.id === currentUser?.id;
  const isAdmin = currentUser?.role === 'admin';

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  if (!user) return (
    <div className="page">
      <div className="alert alert-error">
        {error || 'User not found.'}
        {error && <button className="alert-retry" onClick={fetchUser}>Try again</button>}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Read mode
  // ---------------------------------------------------------------------------
  if (!isEditing) {
    return (
      <div className="page">
        {/* Breadcrumb */}
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <a href="/users" onClick={e => handleBreadcrumbClick(e, '/users')}
            style={{ color: 'var(--accent)', textDecoration: 'none' }}>
            Users
          </a>
          <span>›</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{user.email}</span>
        </div>

        <div className="page-header flex items-center justify-between">
          <div>
            <h1 className="page-title">{user.email}</h1>
            <p className="page-subtitle">
              {isSelf ? 'Your account' : `${user.role} — ${user.enabled ? 'Active' : 'Disabled'}`}
            </p>
          </div>
          {isAdmin && !isSelf && (
            <button className="btn btn-primary" onClick={enterEditMode}>Modify</button>
          )}
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <ReadField label="Email" value={user.email} />
              <ReadField label="Role">
                <span className="badge badge-neutral">{user.role}</span>
              </ReadField>
              <ReadField label="Status">
                {user.enabled
                  ? <span className="badge badge-success"><span className="status-dot" />Active</span>
                  : <span className="badge badge-danger"><span className="status-dot" />Disabled</span>}
              </ReadField>
              <ReadField label="Created" value={new Date(user.created_at).toLocaleString()} mono />
              <ReadField label="Created by" value={user.created_by_email || '—'} />
              <ReadField label="Last updated" value={user.updated_at ? new Date(user.updated_at).toLocaleString() : '—'} mono />
              <ReadField label="Updated by" value={user.updated_by_email || '—'} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit mode
  // ---------------------------------------------------------------------------
  return (
    <div className="page">
      {showCancelDialog && (
        <CancelDialog
          saving={saving}
          onSave={handleSave}
          onDiscard={() => exitEditMode(pendingNavigation)}
          onClose={() => setShowCancelDialog(false)}
        />
      )}

      {/* Breadcrumb */}
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <a href="/users" onClick={e => handleBreadcrumbClick(e, '/users')}
          style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          Users
        </a>
        <span>›</span>
        <a href={`/users/${user.id}`} onClick={e => handleBreadcrumbClick(e, `/users/${user.id}`)}
          style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          {user.email}
        </a>
        <span>›</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>Edit</span>
      </div>

      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Edit {user.email}</h1>
          <p className="page-subtitle">Changes apply immediately after saving.</p>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost" onClick={handleCancelClick} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <div className="card-body">
          {/* Email — read only */}
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input className="form-input" type="email" value={user.email} disabled
              style={{ background: 'var(--content-bg)', color: 'var(--text-secondary)', cursor: 'not-allowed' }} />
            <p className="form-hint">Email cannot be changed as it is used for login.</p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-select" value={formRole}
                onChange={e => setFormRole(e.target.value)}>
                <option value="viewer">Viewer — can view config and history</option>
                <option value="admin">Admin — can edit config and manage users</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <label className="form-label">Account Status</label>
              <label className="toggle" style={{ marginTop: '9px' }}>
                <input type="checkbox" checked={formEnabled}
                  onChange={e => setFormEnabled(e.target.checked)} />
                <div className="toggle-track" />
                <div className="toggle-thumb" />
                <span className="toggle-label">{formEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
          </div>

          <hr className="divider" />

          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Reset Password <span style={{ fontWeight: '400', fontSize: '12px' }}>(leave blank to keep current password)</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={formPassword}
                onChange={e => setFormPassword(e.target.value)}
                placeholder="Min. 12 chars, upper + lower + digit" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" value={formPasswordConfirm}
                onChange={e => setFormPasswordConfirm(e.target.value)}
                placeholder="Repeat new password" />
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom save bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'rgba(247, 248, 250, 0.95)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid var(--border)',
        padding: '16px 0', marginTop: '32px',
        display: 'flex', justifyContent: 'flex-end', gap: '8px'
      }}>
        <button className="btn btn-ghost" onClick={handleCancelClick} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function ReadField({ label, value, mono, children }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
        {label}
      </div>
      {children || (
        <div style={{
          fontSize: '13px',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          color: value ? 'var(--text-primary)' : 'var(--text-tertiary)'
        }}>
          {value || '—'}
        </div>
      )}
    </div>
  );
}
